/**
 * Sign in with a phone number and a code — the same Auth OTP surfaces the
 * other Zoyaar apps use. Who you are (a parent, the person, a therapist)
 * picks the surface; what you may see is decided per profile later.
 */

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { devSignIn, hasSession, otpRequest, otpVerify, type Surface } from '@/core/auth';
import { runtimeConfig } from '@/core/config';
import { latinDigits, toE164 } from '@/core/phone';

const BAD_PHONE = 'شمارهٔ موبایل درست نیست. مثلاً ۰۹۱۲۳۴۵۶۷۸۹ را وارد کنید.';

const SURFACES: { key: Surface; title: string; help: string }[] = [
  { key: 'family', title: 'پدر یا مادر هستم', help: 'پروفایل فرزندم را می‌سازم یا دنبال می‌کنم' },
  { key: 'person', title: 'خودم هستم', help: 'پروفایل خودم (نوجوان یا بزرگ‌سال)' },
  { key: 'specialist', title: 'درمانگر هستم', help: 'با کد اشتراک خانواده وارد پروفایل می‌شوم' },
];

const DEV_ACTORS = [
  { name: 'mother', title: 'مادر اریو' },
  { name: 'father', title: 'پدر اریو' },
  { name: 'slp', title: 'گفتاردرمانگر' },
  { name: 'ot', title: 'کاردرمانگر' },
  { name: 'ario', title: 'خود اریو' },
];

export function SignInPage() {
  const navigate = useNavigate();
  const [surface, setSurface] = useState<Surface | null>(null);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState<{ devCode?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const devUrl = runtimeConfig().devTokenUrl;

  if (hasSession()) {
    void navigate({ to: '/' });
    return null;
  }

  const e164 = toE164(phone);

  const request = async () => {
    if (!surface) return;
    if (!e164) {
      setError(BAD_PHONE);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const ack = await otpRequest(surface, e164);
      setSent({ devCode: ack.devCode });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!surface || !e164) return;
    setBusy(true);
    setError(null);
    try {
      await otpVerify(surface, e164, latinDigits(code).trim());
      await navigate({ to: '/' });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const dev = async (name: string) => {
    setBusy(true);
    setError(null);
    try {
      await devSignIn(name);
      await navigate({ to: '/' });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="page">
      <div style={{ paddingBlock: 24 }}>
        <div className="overline">زویار</div>
        <h1 className="page-title" style={{ fontSize: 'var(--fs-hero)' }}>
          مسیر رشد
        </h1>
        <p className="help">
          قدم‌های کوچک، هر روز. ببینید فرزندتان کجاست، چه چیزی تازه یاد گرفته و قدم بعدی چیست.
        </p>
      </div>

      {!surface && (
        <section className="card">
          <div className="overline">ورود</div>
          {SURFACES.map((s) => (
            <button
              key={s.key}
              type="button"
              className="btn quiet block"
              style={{ justifyContent: 'flex-start', minBlockSize: 64, textAlign: 'start' }}
              onClick={() => setSurface(s.key)}
            >
              <span>
                <strong>{s.title}</strong>
                <br />
                <span className="help">{s.help}</span>
              </span>
            </button>
          ))}
        </section>
      )}

      {surface && !sent && (
        <section className="card">
          <div className="overline">{SURFACES.find((s) => s.key === surface)?.title}</div>
          <label className="field">
            <span>شمارهٔ موبایل</span>
            <input
              className="input machine"
              inputMode="tel"
              placeholder="09xxxxxxxxx"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => e.key === 'Enter' && void request()}
              autoComplete="tel"
            />
          </label>
          {error && <div className="error">{error}</div>}
          <button
            type="button"
            className="btn primary block"
            disabled={busy || latinDigits(phone).replace(/\D/g, '').length < 10}
            onClick={request}
          >
            ارسال کد
          </button>
          <button type="button" className="btn quiet block" onClick={() => setSurface(null)}>
            بازگشت
          </button>
        </section>
      )}

      {surface && sent && (
        <section className="card">
          <div className="overline">کد پیامک‌شده</div>
          {sent.devCode && (
            <div className="notice">
              محیط آزمایشی — کد: <span className="machine">{sent.devCode}</span>
            </div>
          )}
          <label className="field">
            <span>کد</span>
            <input
              className="input machine"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void verify()}
              autoComplete="one-time-code"
              autoFocus
            />
          </label>
          {error && <div className="error">{error}</div>}
          <button
            type="button"
            className="btn primary block"
            disabled={busy || latinDigits(code).replace(/\D/g, '').length < 4}
            onClick={verify}
          >
            ورود
          </button>
          <button type="button" className="btn quiet block" onClick={() => setSent(null)}>
            شماره را عوض می‌کنم
          </button>
        </section>
      )}

      {devUrl && (
        <section className="card" style={{ borderStyle: 'dashed' }}>
          <div className="overline">ورود آزمایشی (فقط لوکال)</div>
          <div className="row">
            {DEV_ACTORS.map((a) => (
              <button
                key={a.name}
                type="button"
                className="btn quiet"
                disabled={busy}
                onClick={() => dev(a.name)}
              >
                {a.title}
              </button>
            ))}
          </div>
          {error && <div className="error">{error}</div>}
        </section>
      )}
    </main>
  );
}
